import type { SearchParams } from '@/server/searchParams.ts';
import { URLParameters } from '@/app/common/urlParameters.ts';
import { whenMultipleValuesTakeTheFirst } from '@/utils/queryUtils.ts';

type ShouldRefreshPageWrapperDataProps = {
  isNewSearch: boolean;
  isBrowserBackNavigation: boolean;
  /** The same navigation is going to ask the server for the results again. */
  willRedoSearch: boolean;
  /** The page data the page holds was built for other places than the URL (see hasSearchPlacesChanged). */
  isSearchChanged: boolean;
};

// The part of the page data the search widget reads for its places (searchInfoDetailData) is built
// from these params. The dates and the participants are not in the list on purpose: the widget takes
// them from the URL and from the searchDefaultData route, so a change of dates alone must not cost
// the page data call (see the app's back button below).
const SEARCH_PLACES_PARAMS = [URLParameters.SEARCH_MODE, URLParameters.ORIGIN, URLParameters.DESTINATION];

// The page data built on the server holds the params as Next gives them, and a repeated query key
// (destination=X&destination=X, from a deeplink) is an array there. The URL read in the browser
// always gives one string, so compare the first value on both sides, as the rest of the code does.
const placeValue = (value: string | string[] | undefined): string => whenMultipleValuesTakeTheFirst(value) || '';

/**
 * Whether the page data the page holds was built for other places than the URL of the next page.
 *
 * A browser navigation does not replace the page data by itself: after a search for A, a search for
 * B from the widget and a browser back to A, the page still holds the data of B while the URL and
 * the results are A, and B's searchInfoDetailData would put the widget on B's destination.
 */
export const hasSearchPlacesChanged = (pageDataSearchParams: SearchParams, nextSearchParams: SearchParams): boolean =>
  SEARCH_PLACES_PARAMS.some((name) => placeValue(pageDataSearchParams[name]) !== placeValue(nextSearchParams[name]));

/**
 * Whether a client-side navigation back to the results page has to refetch the page-wrapper data.
 *
 * The refetch is not a read of something already decided: it recomputes the whole config server
 * side, and the experiment-driven parts are resolved from scratch every time, so a request that
 * cannot reach SAAB answers with the control arm. `appShowCsw` is one of those, and in the app -
 * where the results and detail steps share a single document - the back button is what runs this
 * call. Losing the variant there takes the entire header away, because `showHeader` needs
 * `appShowCsw` in the app (see resolveHeaderVisibility), and nothing puts it back for the rest of
 * the document (MPH-1123).
 *
 * A new search has new inputs, so it needs the new config. A back navigation only does when it is
 * going to redo the search anyway, or when the page data the page holds describes other places
 * than the URL: returning to a list that is already loaded, from the details of one of its hotels,
 * changes nothing the config or the widget places are derived from.
 */
export const shouldRefreshPageWrapperData = ({
  isNewSearch,
  isBrowserBackNavigation,
  willRedoSearch,
  isSearchChanged,
}: ShouldRefreshPageWrapperDataProps): boolean =>
  isNewSearch || (isBrowserBackNavigation && (willRedoSearch || isSearchChanged));
